import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PermissionGate } from "@/components/permission/permission-gate";
import type { ChainDistributionItem } from "../dashboard-selectors";
import type { Wallet } from "@/types";
import {
  UNKNOWN_VALUE_LABEL,
  displayTotal,
  formatUsdValue,
  usdCompact,
  walletValuationValue,
  type ValuationSummary,
} from "@/features/valuation/valuation";

const CHAIN_FILL: Record<string, string> = {
  "Ethereum Mainnet": "var(--ads-accent)",
  "Bitcoin Mainnet": "var(--ads-viz-amber)",
  "Polygon Mainnet": "var(--ads-pending)",
  "Solana Mainnet": "var(--ads-viz-teal)",
  "Ethereum Sepolia": "var(--ads-viz-slate-soft)",
  Others: "var(--ads-viz-slate-faint)",
};

const CHAIN_LABEL: Record<string, string> = {
  "Ethereum Mainnet": "Ethereum",
  "Bitcoin Mainnet": "Bitcoin",
  "Polygon Mainnet": "Polygon",
  "Solana Mainnet": "Solana",
  "Ethereum Sepolia": "Sepolia",
  Others: "Others",
};

/** Why a chain row shows a dash instead of a number. */
const UNVALUED_CHAIN_REASON = "Valuation unavailable";

/**
 * Split 100% across the valued rows in tenths of a percent, largest remainder
 * first, so the displayed column always adds up to exactly 100.0%. Rounding
 * each row on its own (`toFixed(1)`) prints 33.3 + 33.3 + 33.3 = 99.9% for an
 * even three-way split, which reads as a share that went missing.
 */
function shareTenths(values: number[], total: number): number[] {
  const exact = values.map((value) => (value / total) * 1000);
  const tenths = exact.map((value) => Math.floor(value));
  let remaining = 1000 - tenths.reduce((sum, value) => sum + value, 0);
  const byRemainder = exact
    .map((value, index) => ({ index, remainder: value - Math.floor(value) }))
    .sort((a, b) => b.remainder - a.remainder || a.index - b.index);
  for (const { index } of byRemainder) {
    if (remaining <= 0) break;
    tenths[index] += 1;
    remaining -= 1;
  }
  return tenths;
}

interface ChainRow {
  chain: string;
  /** Sum of the chain's valued wallets, or null when nothing on it is valued. */
  value: number | null;
  walletCount: number;
}

export function ChainDistribution({
  items,
  total,
  wallets,
}: {
  items: ChainDistributionItem[];
  total: ValuationSummary;
  wallets: Wallet[];
}) {
  if (wallets.length === 0) {
    return (
      <div className="flex h-full flex-col">
        <h2 className="mb-4 text-sm font-bold uppercase tracking-[0.06em] text-muted">Assets by Chain</h2>
        <div className="flex flex-1 flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-line px-4 text-center text-sm text-muted">
          <p>No wallets yet. Chain distribution will appear once you have funded wallets.</p>
          {/* Same gate as the AUM tile: the way out of the empty state is offered
              only to a session the backend will actually allow to create one. */}
          <PermissionGate permission="wallet:create">
            <Link to="/wallets/new" className="font-semibold text-primary underline">
              Create a wallet
            </Link>
          </PermissionGate>
        </div>
      </div>
    );
  }

  const radius = 40;
  const stroke = 12;
  const circumference = 2 * Math.PI * radius;
  const segments = items.reduce<{ chain: string; pct: number; offset: number; fill: string }[]>(
    (acc, item) => {
      const offset = acc.reduce((sum, s) => sum + s.pct, 0);
      acc.push({
        chain: item.chain,
        pct: item.pct,
        offset,
        fill: CHAIN_FILL[item.chain] ?? "var(--ads-viz-slate)",
      });
      return acc;
    },
    []
  );

  // USD per chain is the sum of that chain's valued wallets. A wallet counts as
  // valued only when it carries a positive valuation — the same rule
  // `getChainDistribution` applies — so these figures cannot drift from the
  // ones the selector feeds the donut, and a missing valuation (null) is never
  // coerced to 0.
  const valuedByChain = new Map<string, { usd: number; walletCount: number }>();
  const walletCountByChain = new Map<string, number>();
  for (const wallet of wallets) {
    walletCountByChain.set(wallet.chain, (walletCountByChain.get(wallet.chain) ?? 0) + 1);
    const value = walletValuationValue(wallet);
    if (value === null || value <= 0) continue;
    const chainTotals = valuedByChain.get(wallet.chain) ?? { usd: 0, walletCount: 0 };
    valuedByChain.set(wallet.chain, {
      usd: chainTotals.usd + value,
      walletCount: chainTotals.walletCount + 1,
    });
  }

  const rows: ChainRow[] = items.map((item) => {
    // "Others" is a bucket the selector assembles from minor chains, so its
    // value can only come from the item; a named chain is summed here.
    const chainTotals = valuedByChain.get(item.chain);
    const value = item.chain === "Others" ? item.value : (chainTotals?.usd ?? item.value);
    return {
      chain: item.chain,
      value: value > 0 ? value : null,
      walletCount: chainTotals?.walletCount ?? 0,
    };
  });

  // A chain whose wallets are all unvalued still holds the customer's wallets:
  // it gets a row with a dash and a reason, rather than being dropped (which
  // reads as "you hold nothing here") or rendered as $0 (which reads as "worth
  // nothing"). A valued chain already inside "Others" keeps its bucket row only.
  const shownChains = new Set(rows.map((row) => row.chain));
  const unvaluedChains = Array.from(walletCountByChain.keys())
    .filter((chain) => !valuedByChain.has(chain) && !shownChains.has(chain))
    .sort((a, b) => (CHAIN_LABEL[a] ?? a).localeCompare(CHAIN_LABEL[b] ?? b));
  for (const chain of unvaluedChains) {
    rows.push({ chain, value: null, walletCount: 0 });
  }

  const valuedRows = rows.filter((row) => row.value !== null);
  const valuedTotal = valuedRows.reduce((sum, row) => sum + (row.value ?? 0), 0);
  // No valuation anywhere means no proportions: without a total the share
  // column is omitted rather than printed as a fabricated "0.0%".
  const tenths = valuedTotal > 0 ? shareTenths(valuedRows.map((row) => row.value ?? 0), valuedTotal) : null;
  let valuedIndex = 0;
  const tenthsByRow = rows.map((row) => {
    if (row.value === null || tenths === null) return null;
    const tenth = tenths[valuedIndex];
    valuedIndex += 1;
    return tenth;
  });

  const trackedChainCount = rows.filter((row) => row.chain !== "Others").length;

  /**
   * OPS-02 (UX audit D13): a ring with a single segment says nothing the legend
   * below does not already say — every valued asset is on one chain, and its
   * share can only be 100%. With exactly one valued chain the tile states that
   * in words instead of drawing a one-slice ring. A chain that holds wallets
   * but no valuation is not that case (there is no valued chain at all), and it
   * keeps the ring's "valuation unavailable" label, so this only fires when the
   * chart would otherwise render exactly one segment.
   */
  const singleChainLabel =
    valuedRows.length === 1
      ? CHAIN_LABEL[valuedRows[0].chain] ?? valuedRows[0].chain
      : null;

  // With nothing valued the donut has no segments, so the label says why rather
  // than trailing off after the colon.
  const aria =
    items.length > 0
      ? items.map((i) => `${CHAIN_LABEL[i.chain] ?? i.chain} ${(i.pct * 100).toFixed(1)}%`).join(", ")
      : UNVALUED_CHAIN_REASON.toLowerCase();

  return (
    <div className="flex h-full flex-col">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-bold uppercase tracking-[0.06em] text-muted">Assets by Chain</h2>
        <Link to="/wallets" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
          View all <ArrowUpRight className="size-3.5" />
        </Link>
      </div>

      <div className="flex flex-1 flex-col items-center justify-between gap-5">
        {singleChainLabel ? (
          // OPS-02 (UX audit D13): one valued chain, no ring — a sentence. The
          // portfolio total keeps its place under it so the tile still answers
          // "how much is there", and the legend below carries the 100% share.
          <div className="flex shrink-0 flex-col items-center gap-2 px-2 text-center">
            <p className="text-sm font-medium text-ink">All valued assets are on {singleChainLabel}</p>
            <div className="flex flex-col items-center">
              <span className="text-base font-bold text-ink">{formatUsdValue(displayTotal(total).value)}</span>
              <span className="text-[9px] uppercase tracking-wider text-muted">Total AUM</span>
            </div>
          </div>
        ) : (
          <div className="relative shrink-0" role="img" aria-label={`Value by chain: ${aria}`}>
            <svg viewBox="0 0 100 100" className="size-36 md:size-40">
              {segments.map(({ chain, pct, offset, fill }) => {
                const dash = pct * circumference;
                return (
                  <circle
                    key={chain}
                    cx="50"
                    cy="50"
                    r={radius}
                    fill="none"
                    stroke={fill}
                    strokeWidth={stroke}
                    strokeDasharray={`${dash.toFixed(2)} ${circumference.toFixed(2)}`}
                    strokeDashoffset={-offset * circumference}
                    transform="rotate(-90 50 50)"
                  />
                );
              })}
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              {/* The same figure the hero shows: `displayTotal` prefers the
                  verified total and falls back to the measured reference total,
                  so the donut's centre can never contradict the KPI. */}
              <span className="text-base font-bold text-ink">{formatUsdValue(displayTotal(total).value)}</span>
              <span className="text-[9px] uppercase tracking-wider text-muted">Total AUM</span>
            </div>
          </div>
        )}

        {/* OPS-02 (UX audit D13): the chart legend. One labelled item per chain
            carrying the same colour, the chain label, its wallet count and its
            share of valued AUM, so the ring's arcs are named and the shares are
            readable without decoding the chart. */}
        <div
          className="w-full space-y-2.5"
          role="list"
          aria-label="Share of valued assets by chain"
        >
          {rows.map((row, index) => {
            const label = CHAIN_LABEL[row.chain] ?? row.chain;
            const tenth = tenthsByRow[index];
            return (
              <div key={row.chain} role="listitem" className="flex items-center justify-between gap-3 text-sm">
                <div className="flex min-w-0 items-center gap-2">
                  <span
                    className="inline-block size-2.5 rounded-full"
                    style={{ backgroundColor: CHAIN_FILL[row.chain] ?? "var(--ads-viz-slate)" }}
                  />
                  <div className="min-w-0">
                    <div className="truncate font-medium text-ink">{label}</div>
                    {row.walletCount > 0 && (
                      <div className="text-[10px] text-muted">
                        {row.walletCount} wallet{row.walletCount === 1 ? "" : "s"}
                      </div>
                    )}
                  </div>
                </div>
                <div className="shrink-0 text-right text-xs tabular-nums">
                  {tenth !== null && (
                    <div className="font-semibold text-ink">{(tenth / 10).toFixed(1)}%</div>
                  )}
                  {row.value !== null ? (
                    <div className="text-muted">{usdCompact.format(row.value)}</div>
                  ) : (
                    <>
                      <div className="font-semibold text-muted">{UNKNOWN_VALUE_LABEL}</div>
                      <div className="text-[10px] text-muted">{UNVALUED_CHAIN_REASON}</div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-2 flex w-full items-center justify-between rounded-xl bg-bg px-4 py-3 text-xs">
          <span className="text-muted">
            {trackedChainCount} chain{trackedChainCount === 1 ? "" : "s"} tracked
          </span>
          <span className="font-bold text-ink">{formatUsdValue(displayTotal(total).value)} total</span>
        </div>
      </div>
    </div>
  );
}
